import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthResponse } from '../models/admin-models';

const STORAGE_TOKEN = 'admin_jwt';
const STORAGE_USER = 'admin_user';

/**
 * AuthService для admin-ng (ADR-030).
 * Использует общий /authenticate endpoint, проверяет platformAdmin флаг.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  login(email: string, password: string): Observable<boolean> {
    const url = `${environment.apiUrl}/authenticate`;
    const body = new HttpParams().set('email', email).set('password', password);
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http.post<AuthResponse>(url, body.toString(), { headers }).pipe(
      map((res) => {
        if (!res.platformAdmin) {
          // не сохраняем токен и кидаем ошибку
          throw new Error('У вас нет прав доступа к админ-панели');
        }
        localStorage.setItem(STORAGE_TOKEN, res.jwt);
        localStorage.setItem(
          STORAGE_USER,
          JSON.stringify({
            id: res.userId,
            email: res.email,
            name: res.name,
            platformAdmin: res.platformAdmin,
          }),
        );
        return true;
      }),
      catchError((err) => throwError(() => (err instanceof Error ? err : new Error(this.extractMessage(err))))),
    );
  }

  /**
   * Сохраняет результат admin SSO (Google) в localStorage. Backend уже проверил
   * что юзер существует и имеет {@code platformAdmin = true}, поэтому здесь
   * просто пишем токен и user-объект.
   */
  saveSsoSession(payload: { token: string; userId: number; email: string; name: string }): void {
    localStorage.setItem(STORAGE_TOKEN, payload.token);
    localStorage.setItem(
      STORAGE_USER,
      JSON.stringify({
        id: payload.userId,
        email: payload.email,
        name: payload.name,
        platformAdmin: true,
      }),
    );
  }

  /** URL входа через Google для admin-флоу. */
  getGoogleSsoLoginUrl(): string {
    // Backend сам определяет admin/обычный флоу по query-параметру admin_flow.
    // Контекст-путь /event-manager уже включён в apiUrl.
    return `${environment.apiUrl}/oauth2/authorization/google?admin_flow=true`;
  }

  logout(): void {
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_USER);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(STORAGE_TOKEN);
  }

  getCurrentUser(): { id: number; email: string; name: string; platformAdmin: boolean } | null {
    const raw = localStorage.getItem(STORAGE_USER);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  isPlatformAdmin(): boolean {
    const user = this.getCurrentUser();
    return !!user && user.platformAdmin === true;
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  private extractMessage(err: { error?: { message?: string }; message?: string }): string {
    return err?.error?.message ?? err?.message ?? 'Ошибка авторизации';
  }
}
